import { useEffect, useState, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable, DataTablePageEvent, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import {
  UnassignedCustomerRequest,
  UnassignedCustomerDto,
  getUnassignedCustomers
} from "../../services/edit-users/edit-users.service";
import CustomerExecutiveService from "../../services/CustomerExecutives/CustomerExecutivesservice";

interface UnassignedCustomerPopupProps {
  visible: boolean;
  onHide: () => void;
  year: number;
  month: number;
}

const UnassignedCustomerPopup: React.FC<UnassignedCustomerPopupProps> = ({
  visible,
  onHide,
  year,
  month
}) => {
  const [customers, setCustomers] = useState<UnassignedCustomerDto[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // sorting state
  const [sortField, setSortField] = useState<string>("name"); // default
  const [sortOrder, setSortOrder] = useState<1 | -1>(1);       // 1 = ASC, -1 = DESC

  // executives dropdown list
  const [executiveList, setExecutiveList] = useState<
    { salesExecutiveId: number; salesExecutiveName: string }[]
  >([]);

  const toast = useRef<Toast>(null);
  const showMessage = (
    summary: string,
    detail: string,
    severity: "success" | "warn" | "error"
  ) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  // fetch customers
  const fetchCustomers = async (
    page: number,
    size: number,
    sortCol = sortField,
    sortOrd = sortOrder
  ) => {
    setLoading(true);
    try {
      const request: UnassignedCustomerRequest = {
        year,
        month,
        pageNumber: page,
        pageSize: size,
        sortColumn: sortCol,
        sortDirection: sortOrd === 1 ? "ASC" : "DESC"
      };

      const response = await getUnassignedCustomers(request);
      setCustomers(response.data);
      setTotalRecords(response.totalRecords);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // fetch executives
  const fetchExecutives = async () => {
    const data = await CustomerExecutiveService.getCustomerExecutiveList();
    setExecutiveList(data);
  };

  useEffect(() => {
    if (visible) {
      fetchCustomers(pageNumber, pageSize);
      fetchExecutives();
    }
  }, [visible, pageNumber, pageSize, year, month]);

  const onPageChange = (e: DataTablePageEvent) => {
    setPageNumber(e.page! + 1);
    setPageSize(e.rows);
  };

  const onSort = (e: DataTableSortEvent) => {
    setSortField(e.sortField as string);
    setSortOrder(e.sortOrder as 1 | -1);
    fetchCustomers(pageNumber, pageSize, e.sortField as string, e.sortOrder as 1 | -1);
  };

  // assign executive editor
  const executiveEditor = (options: any) => (
    <Dropdown
      options={executiveList}
      optionLabel="salesExecutiveName"
      optionValue="salesExecutiveId"
      value={options.rowData.executiveId ?? null}
      onChange={(e) => {
        options.editorCallback(e.value);
        options.rowData.executiveId = e.value;
        options.rowData.customerExecutiveName =
          executiveList.find((x) => x.salesExecutiveId === e.value)
            ?.salesExecutiveName || "";
      }}
      placeholder="Select Executive"
      className="w-full"
    />
  );

  // handle row edit complete
  const onRowEditComplete = async (e: any) => {
    const { newData } = e;
    const customerId = newData.userID;
    const selectedExecutiveId = newData.executiveId;

    if (selectedExecutiveId != null && customerId != null) {
      try {
        const response = await CustomerExecutiveService.updateCustomerExecutive({
          customerId,
          salesExecutiveId: selectedExecutiveId
        });

        if (response) {
          showMessage("Update", "Executive assigned successfully", "success");
          fetchCustomers(pageNumber, pageSize, sortField, sortOrder); // refresh table
        } else {
          showMessage("Update", "Failed to assign executive", "error");
        }
      } catch (error) {
        console.error("Error while updating:", error);
        showMessage("Error", "An error occurred", "error");
      }
    } else {
      showMessage("Missing Data", "Please select an executive", "warn");
    }
  };

  const startIndex = (pageNumber - 1) * pageSize + 1;
  const endIndex = Math.min(pageNumber * pageSize, totalRecords);

  const paginatorTemplate = {
    layout:
      "RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
    CurrentPageReport: () => (
      <span className="mx-3 text-sm text-gray-600">
        Showing {startIndex} to {endIndex} of {totalRecords} entries
      </span>
    )
  };

  return (
    <Dialog
      header={`Unassigned Customers (${totalRecords})`}
      visible={visible}
      style={{ width: "85vw" }}
      onHide={onHide}
      modal
      className="rounded-lg shadow-lg"
    >
      <Toast ref={toast} />
      <div className="p-3 bg-white rounded-lg border border-gray-200">
        <DataTable
          value={customers}
          paginator
          lazy
          rows={pageSize}
          first={(pageNumber - 1) * pageSize}
          totalRecords={totalRecords}
          onPage={onPageChange}
          onSort={onSort}
          sortField={sortField}
          sortOrder={sortOrder}
          loading={loading}
          emptyMessage="No unassigned customers"
          paginatorTemplate={paginatorTemplate}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          editMode="row"
          onRowEditComplete={onRowEditComplete}
          stripedRows
          showGridlines
          size="small"
          className="text-sm"
          rowClassName={() => "hover:bg-gray-50 transition"}
        >
          <Column
            field="name"
            header="Name"
            sortable
            style={{ width: "20%" }}
            headerClassName="bg-gray-100 text-gray-700 text-sm font-semibold"
          />
          <Column
            field="primaryContactNbr"
            header="Contact Number"
            sortable
            style={{ width: "20%" }}
            headerClassName="bg-gray-100 text-gray-700 text-sm font-semibold"
          />
          <Column
            field="areaName"
            header="Area"
            sortable
            style={{ width: "15%" }}
            headerClassName="bg-gray-100 text-gray-700 text-sm font-semibold"
          />
          <Column
            field="subDistrictName"
            header="City"
            sortable
            style={{ width: "15%" }}
            headerClassName="bg-gray-100 text-gray-700 text-sm font-semibold"
          />
          <Column
            field="stateName"
            header="State"
            sortable
            style={{ width: "15%" }}
            headerClassName="bg-gray-100 text-gray-700 text-sm font-semibold"
          />
          <Column
            field="executiveId"
            header="Customer Executive"
            sortable
            style={{ width: "15%" }}
            headerClassName="bg-gray-100 text-gray-700 text-sm font-semibold"
            editor={(options) => executiveEditor(options)}
            body={(rowData) => (
              <span
                className={`px-2 py-1 rounded-md ${
                  rowData.customerExecutiveName
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-red-50 text-red-600 border border-red-200"
                }`}
              >
                {rowData.customerExecutiveName || "Unassigned"}
              </span>
            )}
          />
          <Column
            rowEditor
            header="Assign"
            headerStyle={{ width: "10%" }}
            bodyStyle={{ textAlign: "center" }}
            headerClassName="bg-gray-100 text-gray-700 text-sm font-semibold"
          />
        </DataTable>
      </div>
    </Dialog>
  );
};

export default UnassignedCustomerPopup;
